// Prospecting: the parts of the prospect email that are derived from a scan.
//
// Lifted out of the panel because two places now compose the same message — the send
// dialog after a scan, and the same dialog reopened from a history row — and a second
// copy of the token table is how the two would quietly start disagreeing.
//
// The banding matches v1's prospect preview exactly (ProspectingMode.jsx). It is NOT the
// same as the report's own risk banding in components/report/shapeReport: that one grades
// the report, this one fills a sentence in an email. Keeping v1's thresholds means a
// prospect who gets a report from either interface reads the same words.

export const PROSPECT_SUBJECT = "Security Assessment Report for {domain}";

export const PROSPECT_BODY = `Dear {prospectName},

We have completed a security assessment for {domain}. Please find the detailed report below.

Executive Summary:
- Overall Security Score: {riskScore}/100
- Risk Level: {riskLevel}
- Total Findings: {totalFindings}

Key Findings:
{findingsSummary}`;

// What a template author can actually use. {downloadLink} is deliberately absent: it
// appears in v1's default template but nothing on the client OR the server ever
// substitutes it, so a template using it emails the prospect the literal text
// "{downloadLink}". The report travels as a PDF attachment instead.
export const SUBJECT_TOKENS = "{domain}, {prospectName}, {prospectCompany}, {riskScore}, {riskLevel}, {totalFindings}";
export const BODY_TOKENS = "the six above, plus {findingsSummary}";

export const riskLevelFor = (score) =>
  score >= 75 ? "LOW" : score >= 60 ? "MEDIUM" : score >= 40 ? "HIGH" : "CRITICAL";

// One line per category that found something, with the critical and high counts called
// out — the numbers a salesperson leads the conversation with.
export const findingsSummaryFor = (results) =>
  (results?.categories || [])
    .map((cat) => {
      const findings = cat.findings || [];
      if (!findings.length) return null;
      const at = (sev) => findings.filter((f) => f.severity?.toLowerCase() === sev).length;
      return `• ${cat.name}: ${findings.length} findings (${at("critical")} critical, ${at("high")} high)`;
    })
    .filter(Boolean)
    .join("\n") || "No significant findings detected.";

const totalFindingsFor = (results) =>
  results?.totalFindings ??
  (results?.categories || []).reduce((n, c) => n + (c.findings?.length || 0), 0);

// Fill a subject or body against one scan. Missing values fall back to the same
// placeholders v1 uses, so an unnamed prospect reads "Dear Customer" rather than
// "Dear undefined".
export function fillTokens(text, { results, domain, prospectName, prospectCompany }) {
  const score = results?.riskScore;
  return (text || "")
    .replace(/{domain}/g, domain || "")
    .replace(/{prospectName}/g, prospectName || "Customer")
    .replace(/{prospectCompany}/g, prospectCompany || "Company")
    .replace(/{riskScore}/g, score ?? "N/A")
    .replace(/{riskLevel}/g, riskLevelFor(Number(score) || 0))
    .replace(/{totalFindings}/g, totalFindingsFor(results) || "0")
    .replace(/{findingsSummary}/g, findingsSummaryFor(results));
}
